import { createAction } from 'redux-actions'
import valueEqual from 'value-equal'

import { NEW_SEARCH_TRIGGER_LOCATION, newSearch } from '@/results/store/actions'

import { SearchTypes } from '../search/constants'
import * as searchActions from '../search/store/actions'
import { getReducedSearchState, getUpdateUrl } from '../search/store/selectors'

import sync from './sync'

const triggerActions = [
  // search
  String(newSearch.request),
  // set selection
  ...Object.keys(SearchTypes).map(key => String(searchActions[key].setSelectedId)),
  // navigation
  String(searchActions.navigateUp),
  String(searchActions.selectPrevResultItem),
  String(searchActions.selectNextResultItem)
]

/**
 * @typedef {Object} Location
 * @property {Object} query
 * @property {String} pathname
 */

/**
 * @typedef {Object} LocationSync
 * @property {LocationSync~selector} selector
 * @property {LocationSync~reducer} reducer
 * @property {LocationSync~action} action
 */

/**
 * @callback LocationSync~selector
 * @param {Object} state
 * @returns {any} state
 */

/**
 * @callback LocationSync~reducer
 * @param {Location} location
 * @param {Object} state - State returned by selector
 * @param {Object} previousState
 * @returns {Location} location
 */

/**
 * @callback LocationSync~action
 * @param {Location} location
 * @param {Object} state - State returned by selector
 * @returns {Object} action
 */

export const REPLACE_STATE_ACTION = createAction('REPLACE_STATE_ACTION')

export const reducer = baseReducer => (state, action) => {
  if (action.type === String(REPLACE_STATE_ACTION)) {
    return action.payload
  }
  return baseReducer(state, action)
}

const getLocationState = (state) => sync.map(({ selector }) => selector ? selector(state) : null)
const parseSearch = (
  {
    search,
    state: {
      initial = false,
      data: state
    } = {},
    ...location
  }, {
    includeInitial = false
  } = {}
) => {
  const searchParams = new URLSearchParams(search)
  const query = {}
  for (const key of searchParams.keys()) {
    query[key] = searchParams.getAll(key)
  }
  return {
    query,
    ...(includeInitial ? { initial, state } : null),
    ...location
  }
}

const reduceLocationState = (locationState, currentLocation, previousLocationState = {}) => sync.reduce(
  (location, { reducer = l => l }, key) =>
    reducer(location, locationState[key], previousLocationState[key]),
  currentLocation
)

const stringifySearch = ({ query, ...locationWithoutQuery }) => {
  const searchParams = []
  Object.keys(query).forEach(key => {
    const value = query[key]
    if (Array.isArray(value)) {
      value.forEach(v => searchParams.push([key, v]))
    } else {
      searchParams.push([key, value])
    }
  })
  const search = new URLSearchParams(searchParams).toString()

  return { ...locationWithoutQuery, search }
}

// compute location from state
export const resolve = (previousState, newState, currentLocation) => {
  const parsedLocation = parseSearch(currentLocation)
  const previousLocationState = getLocationState(previousState)
  const newLocationState = getLocationState(newState)
  const newLocation = reduceLocationState(newLocationState, parsedLocation, previousLocationState)
  return stringifySearch(newLocation)
}

export const middlewareFactory = history => {
  /**
   * @return {Location}
   */
  const getLocation = ({ includeInitial = false } = {}) => {
    const { search, state: { initial = false, data: state } = {}, ...location } = history.location
    const searchParams = new URLSearchParams(search)
    const query = {}
    for (const key of searchParams.keys()) {
      query[key] = searchParams.getAll(key)
    }
    return {
      query,
      ...(includeInitial ? { initial, state } : null),
      ...location
    }
  }

  return function locationMiddleware (store) {
    const getState = () => getLocationState(store.getState())
    const shouldUpdateLocation = (newLocation, previousLocation) =>
      getUpdateUrl(store.getState()) && !valueEqual(newLocation, previousLocation)

    let ignoreStateUpdate = false
    let ignoreLocationUpdate = false
    let previousLocation = getLocation()
    let previousState = getState()

    function onStateUpdate (action) {
      if (ignoreStateUpdate) {
        // we are currently updating the store to match location so we don't need to perform the reverse
        return
      }

      // retrieve the new relevant store state
      const newState = getState()
      // compute location based on the modified state
      const newLocation = reduceLocationState(newState, previousLocation, previousState)
      // update the last state
      previousState = newState
      // did the location actually change
      if (!shouldUpdateLocation(newLocation, previousLocation)) { return }

      // cache the new location
      previousLocation = newLocation
      // convert query to search string
      const locationUpdate = stringifySearch(newLocation)

      // push the new state
      ignoreLocationUpdate = true
      try {
        // Replace location when first result is selected after receiving results
        if (action && action.meta && action.meta.autoSelect) {
          history.replace(locationUpdate)
        } else {
          history.push(locationUpdate)
        }
      } finally {
        ignoreLocationUpdate = false
      }
    }

    history.listen(() => {
      if (ignoreLocationUpdate) {
        // we are currently updating location based on store change so we don't need to perform the reverse mapping
        return
      }
      const { initial, state, ...newLocation } = getLocation({ includeInitial: true })

      // special case for initial location - just restore the stored state
      if (initial) {
        ignoreStateUpdate = true
        try {
          store.dispatch(REPLACE_STATE_ACTION(state))
        } finally {
          ignoreStateUpdate = false
        }
        previousLocation = newLocation
        previousState = getState()
        return
      }

      const actionsToDispatch = []
      let triggerSearch = false
      sync.forEach(({ action: actionCreator, shouldTriggerSearch = () => false }, key) => {
        const action = actionCreator ? actionCreator(newLocation, previousState[key], previousLocation) : null
        if (action) {
          actionsToDispatch.push(action)
        }
        triggerSearch |= shouldTriggerSearch(newLocation, previousLocation)
      })

      // update the last location
      previousLocation = newLocation

      if (actionsToDispatch.length === 0) {
        return
      }
      ignoreStateUpdate = true
      try {
        // get the search state before applying the location
        const prevSearchState = getReducedSearchState(store.getState())

        // apply the changes from location
        actionsToDispatch.forEach(action => {
          store.dispatch(action)
        })

        // save the search state after applying the location
        const nextSearchState = getReducedSearchState(store.getState())

        // if search state changed we need to trigger a new search to display
        // proper results
        if (triggerSearch || !valueEqual(prevSearchState, nextSearchState)) {
          store.dispatch(newSearch.trigger(nextSearchState, { trigger: NEW_SEARCH_TRIGGER_LOCATION }))
        }
      } finally {
        ignoreStateUpdate = false
      }

      // we need to update the last state as we just changed it
      previousState = getState()
    })

    // special case for initial page - store the state in location
    if (!getUpdateUrl(store.getState())) {
      ignoreLocationUpdate = true
      try {
        history.replace({
          ...history.location,
          state: { initial: true, data: store.getState() }
        })
      } finally {
        ignoreLocationUpdate = false
      }
    }

    // TODO: remove this when initial search tracking is refactored and doesn't depend on initial state update
    onStateUpdate()

    return next => action => {
      const result = next(action)

      // handle only search and selection
      if (triggerActions.indexOf(action.type) !== -1) {
        onStateUpdate(action)
      }

      return result
    }
  }
}
